import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Label from '../components/ui/Label';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import ListPagination from '../components/ui/ListPagination';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import { api } from '../services/authService';
import { organizationsService } from '../services/organizationsService';
import { ChevronDown, ChevronRight, ChevronUp, Eye, HelpCircle, Search } from 'lucide-react';

const PAGE_SIZE = 15;
const ATTRIBUTES_LIMIT = 10;
const TOP_ATTRIBUTES_TOOLTIP =
  'Only attribute values showing a strong positive predictive potential will be displayed';
const BOTTOM_ATTRIBUTES_TOOLTIP =
  'Only attribute values showing a strong negative predictive potential will be displayed';
const TOOLTIP_WIDTH_PX = 288;

const SORTABLE_COLUMNS = [
  { key: 'offer_id', label: 'Offer ID' },
  { key: 'offer_name', label: 'Offer Name' },
  { key: 'advertiser_name', label: 'Advertiser' },
  { key: 'category', label: 'Category' },
  { key: 'total_click', label: 'Total Clicks' },
  { key: 'total_revenue', label: 'Total Revenue' },
  { key: 'created', label: 'Created' },
];

function SectionTitleWithTooltip({ children, tooltip }) {
  const [visible, setVisible] = React.useState(false);
  const [alignRight, setAlignRight] = React.useState(false);
  const ref = React.useRef(null);

  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const wouldOverflowRight = rect.left + TOOLTIP_WIDTH_PX > window.innerWidth;
      setAlignRight(wouldOverflowRight);
    }
    setVisible(true);
  };

  const handleMouseLeave = () => setVisible(false);

  return (
    <span
      ref={ref}
      className="relative inline-flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <span
        className="ml-1 cursor-help text-gray-400 hover:text-gray-600 flex-shrink-0"
        aria-label={tooltip}
      >
        <HelpCircle className="w-4 h-4" />
      </span>
      <span
        role="tooltip"
        className={`absolute bottom-full mb-1 z-50 w-72 p-2.5 text-xs font-normal text-left text-white bg-gray-900 rounded shadow-lg whitespace-pre-line ${
          visible ? 'block' : 'hidden'
        } ${alignRight ? 'right-0' : 'left-0'}`}
      >
        {tooltip}
      </span>
    </span>
  );
}

function formatDate(value) {
  if (!value) return '—';
  const d = typeof value === 'string' ? value.slice(0, 10) : value;
  return String(d);
}

function formatCount(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toLocaleString();
}

function formatRevenue(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })}`;
}

function formatFeatureLabel(feature) {
  const displayLabel = feature?.display_label?.trim();
  if (displayLabel) return displayLabel;
  const name = feature?.feature;
  if (!name) return '—';
  const spaced = String(name).replaceAll('_', ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatValueLabel(row) {
  const valueLabel = row?.value_label?.trim();
  if (valueLabel) return valueLabel;
  const value = row?.value;
  if (value == null || value === '') return '—';
  const spaced = String(value).replaceAll('_', ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatPercent(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `${n.toFixed(2)}%`;
}

function isMissingValue(value) {
  return value == null || value === '';
}

function allFieldMissing(rows, field) {
  return rows.every((row) => isMissingValue(row?.[field]));
}

/** pct_of_universe is already percent-scaled (e.g. 30.011), not a 0–1 fraction. */
function formatUniverseShare(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `${n.toFixed(2)}%`;
}

function formatTopPairClause(row, featureLabelByName) {
  const featureKey = row?.feature;
  const featureLabel = formatFeatureLabel({
    feature: featureKey,
    display_label: row?.display_label || (featureKey && featureLabelByName?.[featureKey]),
  });
  const valueText = formatValueLabel(row);
  const universePct = row?.pct_of_universe;
  if (universePct != null && universePct !== '') {
    return `${featureLabel} ${valueText} (${formatUniverseShare(universePct)} of the list)`;
  }
  const convertersPct = row?.pct_of_converters;
  if (convertersPct != null && convertersPct !== '') {
    return `${featureLabel} ${valueText} (${formatPercent(convertersPct)} of converters)`;
  }
  return `${featureLabel} ${valueText}`;
}

function formatTopPairsValues(rows, featureLabelByName) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return rows.map((row) => formatTopPairClause(row, featureLabelByName)).join(', ');
}

function formatWarningClause(row, featureLabelByName) {
  if (!row?.feature) return null;
  const featureLabel = formatFeatureLabel({
    feature: row.feature,
    display_label: row.display_label || featureLabelByName?.[row.feature],
  });
  const unknownPct = formatPercent(row.pct_of_converters);
  const unknownText = formatValueLabel(row);
  const parts = [`${featureLabel} is ${unknownText} for ${unknownPct}.`];
  if (row.best_value != null && row.best_value !== '') {
    const bestValueText = formatValueLabel({
      value: row.best_value,
      value_label: row.best_value_label,
    });
    if (row.best_pct_of_universe != null && row.best_pct_of_universe !== '') {
      const bestUniversePct = formatUniverseShare(row.best_pct_of_universe);
      parts.push(
        `${featureLabel} ${bestValueText} converts best but reaches only ${bestUniversePct} of the list.`
      );
    } else {
      parts.push(`${featureLabel} ${bestValueText} converts best.`);
    }
  }
  parts.push(`Do not require ${featureLabel.toLowerCase()} by default.`);
  return parts.join(' ');
}

function compareOfferRows(a, b, field, order) {
  const av = a?.[field];
  const bv = b?.[field];
  const aMissing = av == null || av === '';
  const bMissing = bv == null || bv === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  let cmp = 0;
  if (field === 'offer_id' || field === 'total_click' || field === 'total_revenue') {
    cmp = Number(av) - Number(bv);
  } else if (field === 'created') {
    cmp = String(av).localeCompare(String(bv));
  } else {
    cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
  }
  return order === 'asc' ? cmp : -cmp;
}

function getDetailsMode(offer) {
  if (!offer) return null;
  if (offer.has_metrics) return 'metrics';
  if (offer.has_values) return 'values';
  if (offer.has_vertical) return 'vertical';
  return 'general';
}

function isCategoryDetailsMode(mode) {
  return mode === 'vertical' || mode === 'general';
}

function detailsTitle(offer) {
  const mode = getDetailsMode(offer);
  if (mode === 'values') return 'Offer Details Lite';
  if (mode === 'vertical') {
    return `Offer Details for Vertical "${offer?.category || ''}"`;
  }
  if (mode === 'general') return 'Offer Details General';
  return 'Offer Details';
}

function detailsApiPaths(offer) {
  const id = offer.offer_id;
  const mode = getDetailsMode(offer);
  if (mode === 'values') {
    return {
      features: `/api/v2/offer_reports/${id}/values_features`,
      featureValues: `/api/v2/offer_reports/${id}/values_feature_values`,
    };
  }
  if (mode === 'vertical') {
    return {
      features: `/api/v2/offer_reports/${id}/vertical_features`,
      featureValues: `/api/v2/offer_reports/${id}/vertical_feature_values`,
    };
  }
  if (mode === 'general') {
    return {
      features: `/api/v2/offer_reports/${id}/general_features`,
      featureValues: `/api/v2/offer_reports/${id}/general_feature_values`,
    };
  }
  return {
    features: `/api/v2/offer_reports/${id}/features`,
    topPairs: `/api/v2/offer_reports/${id}/top_pairs`,
    featureValues: `/api/v2/offer_reports/${id}/feature_values`,
  };
}

function OfferReport() {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortByField, setSortByField] = useState('total_revenue');
  const [sortOrder, setSortOrder] = useState('desc');
  const [listOffset, setListOffset] = useState(0);
  const [detailsOffer, setDetailsOffer] = useState(null);
  const [topAttributes, setTopAttributes] = useState([]);
  const [bottomAttributes, setBottomAttributes] = useState([]);
  const [topAttributesLoading, setTopAttributesLoading] = useState(false);
  const [bottomAttributesLoading, setBottomAttributesLoading] = useState(false);
  const [topAttributesError, setTopAttributesError] = useState('');
  const [bottomAttributesError, setBottomAttributesError] = useState('');
  const [topPairs, setTopPairs] = useState({ positive: [], negative: [], warning: null });
  const [topPairsLoading, setTopPairsLoading] = useState(false);
  const [topPairsError, setTopPairsError] = useState('');
  const [expandedAttributeKey, setExpandedAttributeKey] = useState(null);
  const [featureValues, setFeatureValues] = useState({});
  const [featureValuesLoading, setFeatureValuesLoading] = useState(false);
  const [featureValuesError, setFeatureValuesError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const orgs = await organizationsService.getOrganizations();
        if (cancelled) return;
        setOrganizations(orgs);

        const savedSelected = organizationsService.getSelectedOrganization('id');
        if (savedSelected && orgs.some((o) => String(o.id) === String(savedSelected))) {
          setSelectedOrganization(String(savedSelected));
          organizationsService.setSelectedOrganization(savedSelected, 'id', orgs);
        } else if (orgs.length > 0) {
          const defaultId = String(orgs[0].id);
          setSelectedOrganization(defaultId);
          organizationsService.setSelectedOrganization(defaultId, 'id', orgs);
        }
      } catch {
        if (!cancelled) {
          setOrganizations([]);
          setError('Failed to load organizations. Please refresh the page and try again.');
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadRows = useCallback(async () => {
    if (!selectedOrganization) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = {
        organization_id: selectedOrganization,
        sort_by_field: 'total_revenue',
        sort_order: 'desc',
      };
      if (searchQuery) {
        params.search = searchQuery;
      }
      const res = await api.get('/api/v2/offer_reports/', { params });
      setRows(Array.isArray(res.data) ? res.data : []);
      setSortByField('total_revenue');
      setSortOrder('desc');
      setListOffset(0);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : err?.message || 'Failed to load offer reports.'
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedOrganization, searchQuery]);

  useEffect(() => {
    if (!selectedOrganization) return;
    loadRows();
  }, [selectedOrganization, loadRows]);

  useEffect(() => {
    setExpandedAttributeKey(null);
    setFeatureValues({});
    setFeatureValuesError('');
    setFeatureValuesLoading(false);

    if (!detailsOffer || !selectedOrganization) {
      setTopAttributes([]);
      setBottomAttributes([]);
      setTopAttributesError('');
      setBottomAttributesError('');
      setTopAttributesLoading(false);
      setBottomAttributesLoading(false);
      setTopPairs({ positive: [], negative: [], warning: null });
      setTopPairsError('');
      setTopPairsLoading(false);
      return undefined;
    }

    const { features: featuresPath, topPairs: topPairsPath } =
      detailsApiPaths(detailsOffer);

    let cancelled = false;
    const loadAttributes = async (sortOrder, setRows, setLoading, setError, errorFallback) => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(featuresPath, {
          params: {
            organization_id: selectedOrganization,
            sort_order: sortOrder,
            limit: ATTRIBUTES_LIMIT,
          },
        });
        if (!cancelled) {
          setRows(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setError(
            typeof detail === 'string' ? detail : err?.message || errorFallback
          );
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    const loadTopPairs = async () => {
      setTopPairsLoading(true);
      setTopPairsError('');
      try {
        const res = await api.get(topPairsPath, {
          params: { organization_id: selectedOrganization },
        });
        if (!cancelled) {
          setTopPairs({
            positive: Array.isArray(res.data?.positive) ? res.data.positive : [],
            negative: Array.isArray(res.data?.negative) ? res.data.negative : [],
            warning: res.data?.warning ?? null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setTopPairsError(
            typeof detail === 'string'
              ? detail
              : err?.message || 'Failed to load top pairs.'
          );
          setTopPairs({ positive: [], negative: [], warning: null });
        }
      } finally {
        if (!cancelled) {
          setTopPairsLoading(false);
        }
      }
    };
    loadAttributes(
      'asc',
      setTopAttributes,
      setTopAttributesLoading,
      setTopAttributesError,
      'Failed to load top attributes.'
    );
    loadAttributes(
      'desc',
      setBottomAttributes,
      setBottomAttributesLoading,
      setBottomAttributesError,
      'Failed to load bottom attributes.'
    );
    if (topPairsPath) {
      loadTopPairs();
    } else {
      setTopPairs({ positive: [], negative: [], warning: null });
      setTopPairsError('');
      setTopPairsLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, [detailsOffer, selectedOrganization]);

  const expandedAttributeFeature = expandedAttributeKey
    ? expandedAttributeKey.slice(expandedAttributeKey.indexOf(':') + 1)
    : null;

  useEffect(() => {
    if (!expandedAttributeFeature || !detailsOffer || !selectedOrganization) {
      return undefined;
    }
    if (featureValues[expandedAttributeFeature]) return undefined;

    const { featureValues: valuesPath } = detailsApiPaths(detailsOffer);

    let cancelled = false;
    const loadValues = async () => {
      setFeatureValuesLoading(true);
      setFeatureValuesError('');
      try {
        const res = await api.get(valuesPath, {
          params: {
            organization_id: selectedOrganization,
            feature: expandedAttributeFeature,
          },
        });
        if (!cancelled) {
          setFeatureValues((prev) => ({
            ...prev,
            [expandedAttributeFeature]: Array.isArray(res.data) ? res.data : [],
          }));
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setFeatureValuesError(
            typeof detail === 'string'
              ? detail
              : err?.message || 'Failed to load feature values.'
          );
        }
      } finally {
        if (!cancelled) {
          setFeatureValuesLoading(false);
        }
      }
    };
    loadValues();
    return () => {
      cancelled = true;
    };
  }, [
    expandedAttributeFeature,
    detailsOffer,
    selectedOrganization,
    featureValues,
  ]);

  const toggleAttribute = (section, feature) => {
    const key = `${section}:${feature}`;
    setFeatureValuesError('');
    setExpandedAttributeKey((prev) => (prev === key ? null : key));
  };

  const closeDetails = () => {
    setDetailsOffer(null);
    setTopAttributes([]);
    setBottomAttributes([]);
    setTopAttributesError('');
    setBottomAttributesError('');
    setTopAttributesLoading(false);
    setBottomAttributesLoading(false);
    setTopPairs({ positive: [], negative: [], warning: null });
    setTopPairsError('');
    setTopPairsLoading(false);
    setExpandedAttributeKey(null);
    setFeatureValues({});
    setFeatureValuesError('');
    setFeatureValuesLoading(false);
  };

  const applySearch = () => {
    setListOffset(0);
    setSearchQuery(searchInput.trim());
  };

  const featureLabelByName = useMemo(() => {
    const map = {};
    for (const feature of [...topAttributes, ...bottomAttributes]) {
      if (feature?.feature) {
        map[feature.feature] = formatFeatureLabel(feature);
      }
    }
    return map;
  }, [topAttributes, bottomAttributes]);

  const includeValues = useMemo(
    () => formatTopPairsValues(topPairs.positive, featureLabelByName),
    [topPairs.positive, featureLabelByName]
  );
  const excludeValues = useMemo(
    () => formatTopPairsValues(topPairs.negative, featureLabelByName),
    [topPairs.negative, featureLabelByName]
  );
  const warningValue = useMemo(
    () => formatWarningClause(topPairs.warning, featureLabelByName),
    [topPairs.warning, featureLabelByName]
  );

  const sortedOrganizations = useMemo(
    () => [...organizations].sort((a, b) => a.name.localeCompare(b.name)),
    [organizations]
  );

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => compareOfferRows(a, b, sortByField, sortOrder));
    return copy;
  }, [rows, sortByField, sortOrder]);

  const pagedRows = useMemo(
    () => sortedRows.slice(listOffset, listOffset + PAGE_SIZE),
    [sortedRows, listOffset]
  );

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE) || 1);
  const currentPage = Math.floor(listOffset / PAGE_SIZE) + 1;

  useEffect(() => {
    if (listOffset > 0 && listOffset >= sortedRows.length) {
      setListOffset((prev) => Math.max(0, prev - PAGE_SIZE));
    }
  }, [sortedRows.length, listOffset]);

  const toggleSort = (field) => {
    setListOffset(0);
    if (sortByField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortByField(field);
      setSortOrder(
        field === 'offer_name' || field === 'advertiser_name' || field === 'category'
          ? 'asc'
          : 'desc'
      );
    }
  };

  const renderSortableHeader = (label, field, className) => {
    const active = sortByField === field;
    return (
      <th className={`font-medium text-gray-700 ${className}`}>
        <button
          type="button"
          onClick={() => toggleSort(field)}
          className="inline-flex items-center gap-1 hover:text-gray-900"
        >
          {label}
          {active ? (
            sortOrder === 'asc' ? (
              <ChevronUp size={14} />
            ) : (
              <ChevronDown size={14} />
            )
          ) : null}
        </button>
      </th>
    );
  };

  const renderAttributesSection = ({
    section,
    title,
    tooltip,
    attributes,
    loading,
    error,
    emptyMessage,
    loadingMessage,
    isVertical,
    isLite,
  }) => (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-gray-900">
        <SectionTitleWithTooltip tooltip={tooltip}>{title}</SectionTitleWithTooltip>
      </h3>
      {loading ? (
        <p className="text-sm text-gray-500">{loadingMessage}</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : attributes.length === 0 ? (
        <p className="text-sm text-gray-500">{emptyMessage}</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-md">
          <table className="min-w-full text-sm">
            <tbody>
              {attributes.map((feature, index) => {
                const attributeKey = `${section}:${feature.feature}`;
                const isExpanded = expandedAttributeKey === attributeKey;
                const values = featureValues[feature.feature];
                const topValueMissing =
                  feature.top_conv_value == null || feature.top_conv_value === '';
                const topRateMissing =
                  feature.top_conv_rate == null || feature.top_conv_rate === '';
                const topClickersMissing = isMissingValue(feature.top_pct_of_clickers);
                const topUniverseMissing = isMissingValue(feature.top_pct_of_universe);
                const valueText = formatValueLabel({
                  value: feature.top_conv_value,
                  value_label: feature.top_conv_value_label,
                });
                let thirdColumn = '—';
                if (!topValueMissing && !topRateMissing) {
                  thirdColumn = isVertical
                    ? `${valueText} is ${formatPercent(feature.top_conv_rate)} of converters`
                    : `${valueText} converts ${formatPercent(feature.top_conv_rate)}`;
                } else if (isLite && !topValueMissing && !topClickersMissing) {
                  thirdColumn = `${valueText} is ${formatPercent(
                    feature.top_pct_of_clickers
                  )} of clickers`;
                } else if (isLite && !topValueMissing && !topUniverseMissing) {
                  thirdColumn = `${valueText} is ${formatUniverseShare(
                    feature.top_pct_of_universe
                  )} of the list`;
                }
                const showConvRate =
                  !isLite || !values?.length || !allFieldMissing(values, 'conv_rate');
                const showPctOfClickers =
                  isLite &&
                  (!values?.length || !allFieldMissing(values, 'pct_of_clickers'));
                const showPctOfConverters =
                  !isLite ||
                  !values?.length ||
                  !allFieldMissing(values, 'pct_of_converters');
                const showPctOfUniverse =
                  isLite &&
                  (!values?.length || !allFieldMissing(values, 'pct_of_universe'));
                return (
                  <React.Fragment key={attributeKey}>
                    <tr
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleAttribute(section, feature.feature)}
                    >
                      <td
                        className="px-1 py-2 text-gray-500 w-9"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={() => toggleAttribute(section, feature.feature)}
                          className="p-0.5 rounded hover:bg-gray-200"
                          aria-expanded={isExpanded}
                          aria-label={
                            isExpanded ? 'Collapse values' : 'Expand values'
                          }
                        >
                          {isExpanded ? (
                            <ChevronDown size={18} />
                          ) : (
                            <ChevronRight size={18} />
                          )}
                        </button>
                      </td>
                      <td className="pl-1 pr-1 py-2 text-gray-800 whitespace-nowrap tabular-nums w-8">
                        {`${index + 1}.`}
                      </td>
                      <td className="px-3 py-2 text-gray-800 w-[55%] whitespace-normal break-words">
                        {formatFeatureLabel(feature)}
                      </td>
                      <td className="pl-3 pr-3 py-2 text-gray-800 whitespace-nowrap">
                        {thirdColumn}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <td colSpan={4} className="px-3 py-3">
                          {featureValuesLoading && !values ? (
                            <p className="text-sm text-gray-500">Loading values…</p>
                          ) : featureValuesError && !values ? (
                            <p className="text-sm text-red-600">{featureValuesError}</p>
                          ) : !values || values.length === 0 ? (
                            <p className="text-sm text-gray-500">
                              No values for this attribute.
                            </p>
                          ) : (
                            <div className="overflow-x-auto border border-gray-200 rounded-md bg-white">
                              <table className="min-w-full text-sm">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                                      Value
                                    </th>
                                    {isVertical ? (
                                      <>
                                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                                          % of Converters
                                        </th>
                                        <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                                          % of Clickers
                                        </th>
                                      </>
                                    ) : (
                                      <>
                                        {showPctOfClickers ? (
                                          <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                                            % of Clickers
                                          </th>
                                        ) : null}
                                        {showConvRate ? (
                                          <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                                            Conv Rate
                                          </th>
                                        ) : null}
                                        {showPctOfConverters ? (
                                          <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                                            % of Converters
                                          </th>
                                        ) : null}
                                        {showPctOfUniverse ? (
                                          <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                                            % of List
                                          </th>
                                        ) : null}
                                      </>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {values.map((row) => (
                                    <tr
                                      key={row.value}
                                      className="border-b border-gray-100 last:border-b-0"
                                    >
                                      <td className="px-3 py-2 text-gray-800 max-w-[16rem] whitespace-normal break-words">
                                        {formatValueLabel(row)}
                                      </td>
                                      {isVertical ? (
                                        <>
                                          <td className="px-3 py-2 text-gray-800 whitespace-nowrap tabular-nums">
                                            {formatPercent(row.pct_of_converters)}
                                          </td>
                                          <td className="px-3 py-2 text-gray-800 whitespace-nowrap tabular-nums">
                                            {formatPercent(row.pct_of_clickers)}
                                          </td>
                                        </>
                                      ) : (
                                        <>
                                          {showPctOfClickers ? (
                                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap tabular-nums">
                                              {formatPercent(row.pct_of_clickers)}
                                            </td>
                                          ) : null}
                                          {showConvRate ? (
                                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap tabular-nums">
                                              {formatPercent(row.conv_rate)}
                                            </td>
                                          ) : null}
                                          {showPctOfConverters ? (
                                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap tabular-nums">
                                              {formatPercent(row.pct_of_converters)}
                                            </td>
                                          ) : null}
                                          {showPctOfUniverse ? (
                                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap tabular-nums">
                                              {formatUniverseShare(row.pct_of_universe)}
                                            </td>
                                          ) : null}
                                        </>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Offer Reports</h1>
        <p className="mt-1 text-sm text-gray-500">
          Review all offers to identify best and worst performing attributes across campaigns.
        </p>
      </div>

      <Card className="overflow-visible">
        <CardContent className="pt-6 space-y-4 overflow-visible">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-2 min-w-[220px] flex-1 max-w-md">
              <Label htmlFor="organization">Organization</Label>
              <FunctionalSelect
                value={organizationsService.getSelectedOptionValue(
                  sortedOrganizations,
                  selectedOrganization,
                  'id'
                )}
                onValueChange={(value) => {
                  const parsed = organizationsService.parseOrganizationOptionValue(value);
                  setListOffset(0);
                  setSearchInput('');
                  setSearchQuery('');
                  if (parsed) {
                    setSelectedOrganization(parsed.id);
                    organizationsService.setSelectedOrganization(
                      parsed.id,
                      'id',
                      sortedOrganizations
                    );
                  } else {
                    setSelectedOrganization(value);
                    organizationsService.setSelectedOrganization(
                      value,
                      'id',
                      organizations
                    );
                  }
                }}
                placeholder="Select organization"
              >
                {sortedOrganizations.map((org) => (
                  <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
                    {org.name}
                  </SelectItem>
                ))}
              </FunctionalSelect>
            </div>
          </div>

          {selectedOrganization && (
            <div className="flex items-center gap-2 w-full">
              <Input
                type="text"
                placeholder="Search by offer ID or name..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applySearch();
                  }
                }}
                aria-label="Search offers by ID or name"
                className="flex-1 min-w-0"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={applySearch}
                className="shrink-0"
                aria-label="Search"
              >
                <Search className="h-5 w-5 text-white" />
              </Button>
            </div>
          )}

          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          {loading ? (
            <p className="text-sm text-gray-500">Loading offer reports…</p>
          ) : sortedRows.length === 0 ? (
            <p className="text-sm text-gray-500">
              {searchQuery
                ? 'No offers match this search.'
                : 'No offer reports for this organization.'}
            </p>
          ) : (
            <>
              <div className="border border-gray-200 rounded-md overflow-x-auto">
                <table className="min-w-[56rem] w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {SORTABLE_COLUMNS.map((col) =>
                        renderSortableHeader(
                          col.label,
                          col.key,
                          col.key === 'offer_id'
                            ? 'pl-3 pr-3 py-2 text-left border-b whitespace-nowrap'
                            : 'px-3 py-2 text-left border-b whitespace-nowrap'
                        )
                      )}
                      <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap min-w-[5rem]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row) => (
                      <tr
                        key={row.offer_id}
                        className="border-b border-gray-100 hover:bg-gray-50"
                      >
                        <td className="pl-3 pr-3 py-2 text-gray-800 whitespace-nowrap">
                          {row.offer_id}
                        </td>
                        <td
                          className="px-3 py-2 font-medium max-w-[14rem] whitespace-normal break-words text-gray-800"
                          title={row.offer_name || undefined}
                        >
                          {row.offer_name || '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-800 max-w-[12rem] whitespace-normal break-words">
                          {row.advertiser_name || '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                          {row.category || '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-800 whitespace-nowrap tabular-nums">
                          {formatCount(row.total_click)}
                        </td>
                        <td className="px-3 py-2 text-gray-800 whitespace-nowrap tabular-nums">
                          {formatRevenue(row.total_revenue)}
                        </td>
                        <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                          {formatDate(row.created)}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => setDetailsOffer(row)}
                            className="p-1 rounded shrink-0 text-[#0062FB] hover:bg-blue-50"
                            aria-label={`View details for ${row.offer_name || row.offer_id}`}
                            title="View offer details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between gap-3 text-sm text-gray-600">
                <span>
                  {sortedRows.length.toLocaleString()} offer
                  {sortedRows.length === 1 ? '' : 's'}
                  {` · page ${currentPage} of ${totalPages}`}
                </span>
                <ListPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  disabled={loading}
                  onPageChange={(page) => setListOffset((page - 1) * PAGE_SIZE)}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={detailsOffer != null}
        onClose={closeDetails}
        className="w-full max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        <CardHeader className="pb-2 border-b pr-10">
          <CardTitle className="text-lg text-gray-900">
            {detailsTitle(detailsOffer)}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          {detailsOffer ? (
            <div className="overflow-x-auto border border-gray-200 rounded-md">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                      Offer ID
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                      Offer Name
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                      Advertiser
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                      Category
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                      Total Clicks
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                      Total Revenue
                    </th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b whitespace-nowrap">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                      {detailsOffer.offer_id}
                    </td>
                    <td className="px-3 py-2 text-gray-800 max-w-[14rem] whitespace-normal break-words">
                      {detailsOffer.offer_name || '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-800 max-w-[12rem] whitespace-normal break-words">
                      {detailsOffer.advertiser_name || '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                      {detailsOffer.category || '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-800 whitespace-nowrap tabular-nums">
                      {formatCount(detailsOffer.total_click)}
                    </td>
                    <td className="px-3 py-2 text-gray-800 whitespace-nowrap tabular-nums">
                      {formatRevenue(detailsOffer.total_revenue)}
                    </td>
                    <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                      {formatDate(detailsOffer.created)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="text-sm text-gray-800">
            {getDetailsMode(detailsOffer) === 'metrics' ? (
              topPairsLoading ? (
                <p className="text-gray-500">Loading include / exclude summary…</p>
              ) : topPairsError ? (
                <p className="text-red-600">{topPairsError}</p>
              ) : includeValues || excludeValues || warningValue ? (
                <table className="w-full border-collapse text-sm">
                  <tbody>
                    {includeValues ? (
                      <tr>
                        <td className="align-top pr-3 py-0.5 font-bold whitespace-nowrap border-0">
                          Include
                        </td>
                        <td className="align-top py-0.5 border-0">{includeValues}</td>
                      </tr>
                    ) : null}
                    {excludeValues ? (
                      <tr>
                        <td className="align-top pr-3 py-0.5 font-bold whitespace-nowrap border-0">
                          Exclude
                        </td>
                        <td className="align-top py-0.5 border-0">{excludeValues}</td>
                      </tr>
                    ) : null}
                    {warningValue ? (
                      <tr>
                        <td className="align-top pr-3 py-0.5 font-bold whitespace-nowrap border-0">
                          Warning
                        </td>
                        <td className="align-top py-0.5 border-0">{warningValue}</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-500">No include / exclude pairs for this offer.</p>
              )
            ) : detailsOffer?.note?.trim() ? (
              <table className="w-full border-collapse text-sm">
                <tbody>
                  <tr>
                    <td className="align-top pr-3 py-0.5 font-bold whitespace-nowrap border-0">
                      Notes
                    </td>
                    <td className="align-top py-0.5 border-0 whitespace-pre-wrap">
                      {detailsOffer.note}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <p className="text-gray-500">No notes for this offer.</p>
            )}
          </div>

          {renderAttributesSection({
            section: 'top',
            title: 'Top Attributes',
            tooltip: TOP_ATTRIBUTES_TOOLTIP,
            attributes: topAttributes,
            loading: topAttributesLoading,
            error: topAttributesError,
            emptyMessage: 'No top attributes for this offer.',
            loadingMessage: 'Loading top attributes…',
            isVertical: isCategoryDetailsMode(getDetailsMode(detailsOffer)),
            isLite: getDetailsMode(detailsOffer) === 'values',
          })}

          {renderAttributesSection({
            section: 'bottom',
            title: 'Bottom Attributes',
            tooltip: BOTTOM_ATTRIBUTES_TOOLTIP,
            attributes: bottomAttributes,
            loading: bottomAttributesLoading,
            error: bottomAttributesError,
            emptyMessage: 'No bottom attributes for this offer.',
            loadingMessage: 'Loading bottom attributes…',
            isVertical: isCategoryDetailsMode(getDetailsMode(detailsOffer)),
            isLite: getDetailsMode(detailsOffer) === 'values',
          })}

          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={closeDetails}>
              OK
            </Button>
          </div>
        </CardContent>
      </Modal>
    </div>
  );
}

export default OfferReport;
